/**
 * Modal components for PAL Configuration
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatTable } from "@cat-digital-blocks/react/bedrock/components/Table";
import { CatTableBody } from "@cat-digital-blocks/react/bedrock/components/TableBody";
import { CatTableCell } from "@cat-digital-blocks/react/bedrock/components/TableCell";
import { CatTableHeader } from "@cat-digital-blocks/react/bedrock/components/TableHeader";
import { CatTableHeaderCell } from "@cat-digital-blocks/react/bedrock/components/TableHeaderCell";
import { CatTableObject } from "@cat-digital-blocks/react/bedrock/components/TableObject";
import { CatTableRow } from "@cat-digital-blocks/react/bedrock/components/TableRow";
import { CatIconClose } from "@cat-digital-blocks/react/bedrock/components/Icons/Close";
import type {
  SimulationResponse,
  ConfigurationRuleResponse,
} from "@/types/api";
import type { SimulationResults } from "@/types/simulation";
import { REVERSE_OPERATOR_MAP } from "@/shared/utils/operatorUtils";
import { BaseModal } from "./BaseModal";

interface ModalProps {
  styles: Record<string, string>;
  onClose: () => void;
}

/**
 * New Simulation Modal
 */
interface NewSimulationModalProps extends ModalProps {
  simulationName: string;
  onSimulationNameChange: (name: string) => void;
  onConfirm: () => void;
  existingDraftName?: string;
}

export const NewSimulationModal: React.FC<NewSimulationModalProps> = ({
  styles,
  onClose,
  simulationName,
  onSimulationNameChange,
  onConfirm,
  existingDraftName,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("simulation.createNew")}
          </CatHeading>
        </div>
        <div className={styles.modalBody}>
          <p>{t("simulation.enterName")}</p>
          {existingDraftName && (
            <p className={styles.modalWarning}>
              ⚠️ {t("simulation.overrideDraftWarning")}
              {existingDraftName ? ` "${existingDraftName}"` : ""}.
            </p>
          )}
          <div className={styles.modalFormControl}>
            <CatInputField
              fieldId="simulationName"
              label={t("simulation.simulationName")}
              value={simulationName}
              onBlChange={(e) => onSimulationNameChange(e.detail?.value ?? "")}
              placeholder={t("simulation.namePlaceholder")}
              autoFocus
              size="sm"
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <CatButton variant="contrast" onClick={onClose}>
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={onConfirm}
            disabled={!simulationName.trim()}
          >
            {t("common.create")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Unsaved Changes Modal
 */
interface UnsavedChangesModalProps extends ModalProps {
  onDiscard: () => void;
  isBackNavigation?: boolean;
}

export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  styles,
  onClose,
  onDiscard,
  isBackNavigation,
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal
      styles={styles}
      onClose={onClose}
      title={t("simulation.unsavedChanges")}
      primaryButtonText={t("simulation.discardChanges")}
      onPrimaryClick={onDiscard}
      secondaryButtonText={t("common.cancel")}
    >
      <p>
        {isBackNavigation
          ? t("simulation.unsavedChangesBackWarning")
          : t("simulation.unsavedChangesWarning")}
      </p>
    </BaseModal>
  );
};

/**
 * New Simulation Confirmation Modal (when editing existing draft)
 */
interface NewSimulationConfirmModalProps extends ModalProps {
  simulationName: string;
  onSimulationNameChange: (name: string) => void;
  onConfirm: () => void;
  currentDraftName?: string;
}

export const NewSimulationConfirmModal: React.FC<
  NewSimulationConfirmModalProps
> = ({
  styles,
  onClose,
  simulationName,
  onSimulationNameChange,
  onConfirm,
  currentDraftName,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("simulation.startNewQuestion")}
          </CatHeading>
        </div>
        <div className={styles.modalBody}>
          <p>{t("simulation.startNewWarning")}</p>
          <p className={styles.modalWarning}>
            {t("simulation.currentDraft")}: {currentDraftName}
          </p>
          <div className={styles.modalFormControl}>
            <CatInputField
              fieldId="newSimulationName"
              label={t("simulation.newSimulationName")}
              value={simulationName}
              onBlChange={(e) => onSimulationNameChange(e.detail?.value ?? "")}
              placeholder={t("simulation.namePlaceholder")}
              autoFocus
              size="sm"
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <CatButton variant="contrast" onClick={onClose}>
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={onConfirm}
            disabled={!simulationName.trim()}
          >
            {t("simulation.startNew")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Simulation Results Modal
 */
interface ResultsModalProps extends ModalProps {
  results: SimulationResults;
}

export const ResultsModal: React.FC<ResultsModalProps> = ({
  styles,
  onClose,
  results,
}) => {
  const { t } = useTranslation();

  const totalDollarImpact = results.rows.reduce(
    (sum, row) => sum + row.dollarImpact,
    0,
  );
  const totalCurrentRecords = results.rows.reduce(
    (sum, row) => sum + row.currentRecords,
    0,
  );
  const totalNewRecords = results.rows.reduce(
    (sum, row) => sum + row.newRecords,
    0,
  );
  const totalDiff = totalNewRecords - totalCurrentRecords;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.resultsModal}>
        <div className={styles.resultsModalHeader}>
          <div>
            <CatHeading variant="title" className={styles.resultsModalTitle}>
              {results.dealerName} ({results.dealerCode})
            </CatHeading>
            <p className={styles.resultsModalSubtitle}>
              {t("results.simulation")}: {results.simulationName}
            </p>
          </div>
          <CatButton
            variant="ghost-danger"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CatIconClose size="sm" />
          </CatButton>
        </div>
        <div className={styles.resultsModalBody}>
          <p className={styles.resultsNote}>{t("results.note")}</p>
          <p className={styles.resultsSummary}>{results.summary}</p>
          <div className={styles.resultsTableWrapper}>
            <CatTableObject>
              <CatTable className={styles.resultsTable}>
                <CatTableHeader>
                  <CatTableRow>
                    <CatTableHeaderCell width="25%">
                      {t("results.workstream")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="18%" align="right">
                      {t("results.currentRecords")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="18%" align="right">
                      {t("results.newRecords")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="18%" align="right">
                      {t("results.diffRecords")}
                    </CatTableHeaderCell>
                    <CatTableHeaderCell width="21%" align="right">
                      {t("results.dollarImpact")}
                    </CatTableHeaderCell>
                  </CatTableRow>
                </CatTableHeader>
                <CatTableBody>
                  {results.rows.map((row, index) => (
                    <CatTableRow key={index}>
                      <CatTableCell>{row.workstream}</CatTableCell>
                      <CatTableCell align="right">
                        {row.currentRecords.toLocaleString()}
                      </CatTableCell>
                      <CatTableCell align="right">
                        {row.newRecords.toLocaleString()}
                      </CatTableCell>
                      <CatTableCell align="right">
                        {(row.newRecords - row.currentRecords).toLocaleString()}
                      </CatTableCell>
                      <CatTableCell
                        align="right"
                        className={
                          row.dollarImpact < 0
                            ? styles.negativeValue
                            : row.dollarImpact > 0
                              ? styles.positiveValue
                              : ""
                        }
                      >
                        {row.dollarImpact < 0
                          ? "-"
                          : row.dollarImpact > 0
                            ? "+"
                            : ""}
                        ${Math.abs(row.dollarImpact).toLocaleString()}
                      </CatTableCell>
                    </CatTableRow>
                  ))}
                  <CatTableRow className={styles.totalRow}>
                    <CatTableCell>
                      <strong>{t("results.total")}</strong>
                    </CatTableCell>
                    <CatTableCell align="right">
                      <strong>{totalCurrentRecords.toLocaleString()}</strong>
                    </CatTableCell>
                    <CatTableCell align="right">
                      <strong>{totalNewRecords.toLocaleString()}</strong>
                    </CatTableCell>
                    <CatTableCell align="right">
                      <strong>{totalDiff.toLocaleString()}</strong>
                    </CatTableCell>
                    <CatTableCell
                      align="right"
                      className={
                        totalDollarImpact < 0 ? styles.negativeValue : ""
                      }
                    >
                      <strong>
                        {totalDollarImpact < 0 ? "-" : "+"}$
                        {Math.abs(totalDollarImpact).toLocaleString()}
                      </strong>
                    </CatTableCell>
                  </CatTableRow>
                </CatTableBody>
              </CatTable>
            </CatTableObject>
          </div>
        </div>
        <div className={styles.resultsModalFooter}>
          <CatButton variant="primary" onClick={onClose}>
            {t("common.close")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

/**
 * View Changes Modal
 */
interface ChangesModalProps extends ModalProps {
  simulation: SimulationResponse;
  rules: ConfigurationRuleResponse[];
  isLoading?: boolean;
}

export const ChangesModal: React.FC<ChangesModalProps> = ({
  styles,
  onClose,
  simulation,
  rules,
  isLoading,
}) => {
  const { t } = useTranslation();

  // Helper function to format values with boolean init caps
  const formatValue = (value: unknown): string => {
    if (value === true || String(value).toLowerCase() === "true") return "True";
    if (value === false || String(value).toLowerCase() === "false")
      return "False";
    return String(value);
  };

  // Helper function to convert operator text to display format
  const formatOperator = (operator: string): string => {
    return (
      REVERSE_OPERATOR_MAP[operator] ??
      REVERSE_OPERATOR_MAP[operator.toUpperCase()] ??
      operator
    );
  };

  // Helper function to extract conditions from nested structures
  const extractConditions = (
    data: Record<string, unknown>,
  ): { field: string; operator: string; value: string }[] => {
    if (data.field && data.operator) {
      const val = Array.isArray(data.value)
        ? (data.value as unknown[]).map((v) => formatValue(v)).join(", ")
        : data.value != null
          ? formatValue(data.value)
          : "-";
      return [
        {
          field: String(data.field),
          operator: String(data.operator),
          value: val,
        },
      ];
    }

    const nestedRules = (data.rules || data.conditions) as
      | Record<string, unknown>[]
      | undefined;
    if (nestedRules && Array.isArray(nestedRules)) {
      return nestedRules.flatMap((r) => extractConditions(r));
    }

    return [];
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.resultsModal}>
        <div className={styles.resultsModalHeader}>
          <div>
            <CatHeading variant="title" className={styles.resultsModalTitle}>
              {t("changes.title")}
            </CatHeading>
            <p className={styles.resultsModalSubtitle}>
              {simulation.simulationName || simulation.simulationId}
            </p>
          </div>
          <CatButton
            variant="ghost-danger"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CatIconClose size="sm" />
          </CatButton>
        </div>
        <div className={styles.resultsModalBody}>
          {isLoading ? (
            <p>{t("common.loading")}</p>
          ) : rules.length === 0 ? (
            <p>{t("changes.noChanges")}</p>
          ) : (
            <div className={styles.resultsTableWrapper}>
              <CatTableObject>
                <CatTable className={styles.resultsTable}>
                  <CatTableHeader>
                    <CatTableRow>
                      <CatTableHeaderCell width="15%">
                        {t("results.workstream")}
                      </CatTableHeaderCell>
                      <CatTableHeaderCell width="30%">
                        {t("changes.description")}
                      </CatTableHeaderCell>
                      <CatTableHeaderCell width="25%">
                        {t("parameters.fieldName")}
                      </CatTableHeaderCell>
                      <CatTableHeaderCell width="10%">
                        {t("parameters.operator")}
                      </CatTableHeaderCell>
                      <CatTableHeaderCell width="20%">
                        {t("parameters.value")}
                      </CatTableHeaderCell>
                    </CatTableRow>
                  </CatTableHeader>
                  <CatTableBody>
                    {rules.flatMap((rule, ruleIndex) => {
                      const ruleData = rule.simulationRule || {};
                      const conditions = extractConditions(ruleData);

                      if (conditions.length === 0) {
                        return (
                          <CatTableRow key={rule.simulationRuleId || ruleIndex}>
                            <CatTableCell>{rule.workstream}</CatTableCell>
                            <CatTableCell>{rule.description}</CatTableCell>
                            <CatTableCell>-</CatTableCell>
                            <CatTableCell>
                              {ruleData.operator
                                ? formatOperator(String(ruleData.operator))
                                : "-"}
                            </CatTableCell>
                            <CatTableCell>-</CatTableCell>
                          </CatTableRow>
                        );
                      }

                      return conditions.map((condition, condIndex) => (
                        <CatTableRow
                          key={`${
                            rule.simulationRuleId || ruleIndex
                          }-${condIndex}`}
                        >
                          <CatTableCell>{rule.workstream}</CatTableCell>
                          <CatTableCell>{rule.description}</CatTableCell>
                          <CatTableCell>{condition.field}</CatTableCell>
                          <CatTableCell>
                            {formatOperator(condition.operator)}
                          </CatTableCell>
                          <CatTableCell>{condition.value}</CatTableCell>
                        </CatTableRow>
                      ));
                    })}
                  </CatTableBody>
                </CatTable>
              </CatTableObject>
            </div>
          )}
        </div>
        <div className={styles.resultsModalFooter}>
          <CatButton variant="primary" onClick={onClose}>
            {t("common.close")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};
